import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    myPublicKey: String,
    otherPublicKey: String,
    chewId: String
  }

  static sessionKey = null; // 🛡️ Lock the session key here

  async connect() {
    console.log("📡 ECDH connected");

    console.log("🔒 My public key:", this.myPublicKeyValue);
    console.log("🔓 Their public key:", this.otherPublicKeyValue);

    // Only generate a new keypair if we don't have a session key yet
    if (!this.constructor.sessionKey) {
      await this.generateNewKeypair();
      await this.deriveSessionKey();
    } else {
      console.log("🔒 Using existing session key.");
      window.sessionSharedKey = this.constructor.sessionKey;
      window.dispatchEvent(new CustomEvent("sessionKeyReady"));
    }
  }

  async deriveSessionKey() {
    const myPrivateKey = await this.loadPrivateKey();
    const otherPublicKey = await this.importPublicKey(this.otherPublicKeyValue);

    const sharedBits = await crypto.subtle.deriveBits(
      { name: "ECDH", public: otherPublicKey },
      myPrivateKey,
      256
    );

    const sharedHash = await crypto.subtle.digest("SHA-256", sharedBits);

    const sessionKey = await crypto.subtle.importKey(
      "raw",
      sharedHash,
      { name: "AES-GCM" },
      true,
      ["encrypt", "decrypt"]
    );

    // Save it locked
    this.constructor.sessionKey = sessionKey;
    window.sessionSharedKey = sessionKey;

    window.dispatchEvent(new CustomEvent("sessionKeyReady"));
    console.log("✅ Session key derived and dispatched.");
  }

  async generateNewKeypair() {
    console.log("🔧 Generating new ECDH keypair (forced fresh)...");
    const keyPair = await crypto.subtle.generateKey(
      { name: "ECDH", namedCurve: "P-256" },
      true,
      ["deriveBits"]
    );

    const jwkPrivate = await crypto.subtle.exportKey("jwk", keyPair.privateKey);
    delete jwkPrivate.key_ops;

    localStorage.setItem("ecdh:privateKey", JSON.stringify(jwkPrivate));
    const rawPubKey = await crypto.subtle.exportKey("raw", keyPair.publicKey);
    const pubB64 = btoa(String.fromCharCode(...new Uint8Array(rawPubKey)));

    console.log("📬 Public key to update your backend if needed:", pubB64);
  }

  async loadPrivateKey() {
    const storedKey = localStorage.getItem("ecdh:privateKey");
    if (!storedKey) throw new Error("🔑 No private key in localStorage");

    const jwk = JSON.parse(storedKey);
    delete jwk.key_ops;

    return await crypto.subtle.importKey(
      "jwk",
      jwk,
      { name: "ECDH", namedCurve: "P-256" },
      false,
      ["deriveBits"]
    );
  }

  async importPublicKey(base64String) {
    const raw = Uint8Array.from(atob(base64String), c => c.charCodeAt(0));
    return await crypto.subtle.importKey(
      "raw",
      raw,
      { name: "ECDH", namedCurve: "P-256" },
      true,
      []
    );
  }

  static resetSessionKey() {
    console.log("🔄 Session key reset requested.");
    this.sessionKey = null;
  }
}
